<template>
  <div class="paperwork-container" v-if="showPage">
    <QuestionnaireMenu class="menu" :logo="logoUrl" :languages="languageOptions"></QuestionnaireMenu>
    <div class="banner">
      <img :src="bannerUrl" alt="">
    </div>
    <questionnaire-message class="message" :messages="messages"></questionnaire-message>
    <div class="title">{{ currentStep === 0 ? pageTitle : getText(steps[currentStep - 1]['title']) }}</div>
    <div class="subtitle">{{ userName }}</div>
    <questionnaire-divider />
    <ul v-if="currentStep === 0" class="step-zero-div">
      <li>{{ $t('questionnaire.message.questionnaireStepZeroText1') }}</li>
      <li>{{ $t('questionnaire.message.questionnaireStepZeroText2') }}</li>
      <li>{{ $t('questionnaire.message.questionnaireStepZeroText3') }}</li>
    </ul>
    <questionnaire-step class="steps-component" v-if="currentStep > 0  && steps.length > 1" :steps="steps" :currentStep="currentStep" :progressIcons="stepsProgressIcons" :defaultColor="themeColor"></questionnaire-step>
    <div class="step-notice main-color-style common-font custom-color" v-if="currentStep > 0 && steps[currentStep - 1].notice">{{ getText(steps[currentStep - 1].notice) }}</div>
    <div class="input-form-div">
      <div v-if="!stepQuestions[`step${currentStep}`]" class="empty-div"></div>
      <div v-for="(question, index) in stepQuestions[`step${currentStep}`]" v-show="showQuestion(stepQuestions[`step${currentStep}`], question)" :key="`step${currentStep}${index}`">
        <questionnaire-input
          v-if="question['type'] === 'TEXT'"
          v-model="question['value']"
          :ref="`question${question['id']}`"
          :label="getText(question['title'])"
          :placeholder="getText(question['placeholder'])"
          :regex="getRegex(question).regex"
          :tooltip="getRegex(question).tip"
          :isRequired="getRequired(question)"
          :notice="getText(question['notice'])"
          :maxlength="128"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''" />
        <questionnaire-dropdown
          v-if="question['type'] === 'SINGLE'"
          v-model="question['value']"
          :ref="`question${question['id']}`"
          :label="getText(question['title'])"
          :options="getOptions(question['options'])"
          :placeholder="getText(question['placeholder'])"
          :isRequired="getRequired(question)"
          :notice="getText(question['notice'])"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''"
          @change="onDropdownChange($event, question)"></questionnaire-dropdown>
        <questionnaire-multiple
          v-if="question['type'] === 'MULTIPLE'"
          v-model="question['value']"
          :ref="`question${question['id']}`"
          :label="getText(question['title'])"
          :options="getOptions(question['options'])"
          :placeholder="getText(question['placeholder'])"
          :isRequired="getRequired(question)"
          :notice="getText(question['notice'])"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''"
          @change="onDropdownChange($event, question)"></questionnaire-multiple>
        <questionnaire-datetime-picker
          v-if="question['type'] === 'DATE'"
          v-model="question['value']"
          :ref="`question${question['id']}`"
          :label="getText(question['title'])"
          :isRequired="getRequired(question)"
          :placeholder="getText(question['placeholder'])"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''" ></questionnaire-datetime-picker>
        <questionnaire-checkbox-option
          v-if="question['type'] === 'LINK'"
          v-model="question['value']"
          align="left"
          :ref="`question${question['id']}`"
          :text="getText(question['title'])"
          :isRequired="getRequired(question)"
          :hasLinkDescription="checkLinkQuestionHasDescription(question)"
          :linkText="getLinkConfigText(question, 'link_text')"
          :linkUrl="getLinkConfigText(question, 'link_url')"></questionnaire-checkbox-option>
        <questionnaire-input
          v-if="question['type'] === 'ATTACHMENT'"
          v-model="question['value']"
          type="file"
          :ref="`question${question['id']}`"
          :fileName="question['name']"
          :isRequired="getRequired(question)"
          :label="getText(question['title'])"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''"
          :upload="question['uploadFunc']"
          :questionId="question['id']"
          @input="handleFileChange($event, question)"></questionnaire-input>
        <questionnaire-stars
          v-if="question['type'] === 'STARS'"
          v-model="question['value']"
          :ref="`question${question['id']}`"
          :label="getText(question['title'])"
          :placeholder="getText(question['placeholder'])"
          :isRequired="getRequired(question)"
          :notice="getText(question['notice'])"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''"
          @change="onStarsChange($event, question)"></questionnaire-stars>
        <questionnaire-NPS
          v-if="question['type'] === 'NPS'"
          v-model="question['value']"
          :ref="`question${question['id']}`"
          :label="getText(question['title'])"
          :placeholder="getText(question['placeholder'])"
          :isRequired="getRequired(question)"
          :notice="getText(question['notice'])"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''"
          @change="onNPSChange($event, question)"></questionnaire-NPS>
        <questionnaire-date-range
          v-if="question['type'] === 'DATE_RANGE'"
          v-model="question['value']"
          :ref="`question${question['id']}`"
          :label="getText(question['title'])"
          :placeholder="getText(question['placeholder'])"
          :isRequired="getRequired(question)"
          :config="question['config']"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''"></questionnaire-date-range>
        <questionnaire-repeat-group
          v-if="question['type'] === 'REPEAT_GROUP'"
          v-model="question['value']"
          :ref="`question${question['id']}`"
          :label="getText(question['title'])"
          :isRequired="getRequired(question)"
          :config="question['config']"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''"></questionnaire-repeat-group>
        <questionnaire-signature
          v-if="question['type'] === 'SIGN'"
          v-model="question['value']"
          :ref="`question${question['id']}`"
          :label="getText(question['title'])"
          :isRequired="getRequired(question)"
          :candidateId="candidateId"
          :jobId="jobId"
          :questionId="question['id']"
          :isEditable="notEditableIds.includes(question['id']) ? 'false': ''"
          @upload-error="onSignatureUploadError"></questionnaire-signature>
      </div>
    </div>
    <div class="next-button">
      <questionnaire-button v-if="currentStep > 0" @click.native="toLastStep" :text="$t('questionnaire.btn.back')" type='light' width="normal" class="btn-back"></questionnaire-button>
      <questionnaire-button @click.native="handleCancelBtnClick" :text="$t('questionnaire.btn.cancel')" type='light' width="normal" class="btn-cancel"></questionnaire-button>
      <questionnaire-button @click.native="toNextStep" :text="(currentStep > 0 && currentStep === steps.length) ? $t('questionnaire.btn.finish') : $t('questionnaire.btn.next')" type='normal' width="normal" class="btn-next"></questionnaire-button>
    </div>
  </div>
</template>
<script>
import QuestionnaireInput from '@/components/questionnaire/form/Input'
import QuestionnaireDropdown from '@/components/questionnaire/form/Dropdown'
import QuestionnaireDivider from '@/components/questionnaire/form/Divider'
import QuestionnaireDatetimePicker from '@/components/questionnaire/form/DatetimePicker'
import QuestionnaireStep from '@/components/questionnaire/form/Step'
import QuestionnaireCheckboxOption from '@/components/questionnaire/form/CheckboxOption'
import QuestionnaireButton from '@/components/questionnaire/form/Button'
import QuestionnaireMenu from '@/components/questionnaire/form/Menu'
import QuestionnaireMessage from '@/components/questionnaire/form/Message'
import QuestionnaireStars from '@/components/questionnaire/form/Stars'
import QuestionnaireNPS from '@/components/questionnaire/form/NPS'
import QuestionnaireMultiple from '@/components/questionnaire/form/Multiple'
import QuestionnaireDateRange from '@/components/questionnaire/form/DateRange.vue'
import QuestionnaireRepeatGroup from '@/components/questionnaire/form/RepeatGroup.vue'
import QuestionnaireSignature from '@/components/questionnaire/form/Signature.vue'
import { questionnaireMixin } from '@/components/questionnaire/form/mixin.js'
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import * as qiniu from 'qiniu-js'
import time from '@/utils/time'
import moment from 'moment'
import { deviceType } from '@/utils/common'

const needSortOptionsQuestionId = [9, 11, 12, 25]

export default {
  mixins: [questionnaireMixin],
  components: {
    QuestionnaireInput,
    QuestionnaireDropdown,
    QuestionnaireDivider,
    QuestionnaireDatetimePicker,
    QuestionnaireStep,
    QuestionnaireCheckboxOption,
    QuestionnaireButton,
    QuestionnaireMenu,
    QuestionnaireMessage,
    QuestionnaireStars,
    QuestionnaireNPS,
    QuestionnaireMultiple,
    QuestionnaireDateRange,
    QuestionnaireRepeatGroup,
    QuestionnaireSignature
  },
  metaInfo () {
    return {
      title: this.pageTitle
    }
  },
  data () {
    return {
      themeColor: '',
      bannerUrl: '',
      userName: '',
      invitationCode: '',
      account: {},
      showPage: false,
      notEditableIds: [],
      stepsProgressIcons: {
        finishedIcon: { color: 'gray', address: 'iconduigou' },
        currentIcon: { color: '#8968CD', address: 'icondot' },
        unfinishedIcon: { color: 'gray', address: 'iconshixinxiaoyuanquan' }
      },
      stepProgressIcons: [
        require('@/assets/image/questionnaire/common/step-finished.png'),
        require('@/assets/image/questionnaire/common/step-current.png'),
        require('@/assets/image/questionnaire/common/step-unfinished.png')
      ],
      steps: [],
      stepQuestions: {},
      questionPidMap: {},
      dynamicQuestionMap: {
        _allChildren: []
      },
      fileUploadQuestion: {}
    }
  },
  computed: {
    questionnaireId () {
      return this.$route.params.questionnaireId
    },
    currentStep () {
      return this.$route.query.step ? Number(this.$route.query.step) : 0
    },
    invitationId () {
      return this.$route.params.invitationId
    },
    questionnaireType () {
      return this.$parent.invitationInfo.type
    },
    candidateId () {
      return (this.$parent.invitationInfo.candidate || {}).id
    },
    jobId () {
      return (this.$parent.invitationInfo.job || {}).id
    },
    pageTitle () {
      return this.getQuestionnaireLabelByType(this.questionnaireType)
    }
  },
  async created () {
    const isMobile = deviceType.isMobile
    this.bannerUrl = isMobile ? '' : ''
    this.userName = this.$parent.userName
    this.invitationCode = this.$parent.invitationCode
    this.$eventBus.$emit('showLoading', true)
    await this.getSteps(this.questionnaireId)
    if (this.currentStep > 0 && this.currentStep - 1 < this.steps.length) {
      await this.getSingleStep(this.questionnaireId, (this.currentStep), this.invitationId)
    }
    this.$eventBus.$emit('showLoading', false)
    this.showPage = true
    const invitationInfo = this.$parent.invitationInfo
    if (invitationInfo.custom_color && invitationInfo.custom_color.startsWith('#')) {
      this.themeColor = invitationInfo.custom_color
    }
    this.bannerUrl = this.$parent.headerImage
  },
  methods: {
    ...mapActions('questionnaire', ['getQuestionnaireSteps', 'getStepQuestions', 'answerQuestions']),
    ...mapGetters('account', ['getCurrentAccount']),
    getRegex (question) {
      const regex = ''
      const tip = ''
      return { regex: regex, tip: tip }
    },
    getRequired (question) {
      const isRequired = question.is_required
      return isRequired
    },
    async uploadFileForQuestion (questionId, uploader) {
      if (!questionId) {
        this.showMessage(this.$t('questionnaire.error.uploadError'), 'error', 3)
        return
      }
      this.$store.dispatch('common/getQiniuUploadToken').then((resp) => {
        const token = resp.token
        const selectedFiles = this.$refs[`question${questionId}`][0].$refs['file-input'].files
        if (selectedFiles.length === 1) {
          const selectedFile = selectedFiles[0]
          const fileName = selectedFile.name
          const observable = qiniu.upload(selectedFile, undefined, token, {}, { region: qiniu.region.z0 })
          uploader.inUploadProgress = true
          observable.subscribe(
            (resp) => {
              // show progress
              this.fileUploadQuestion[questionId] = 'uploading'
            }, (resp) => {
              resp && resp.message && this.showMessage(this.$t('questionnaire.error.uploadError'), 'error', 3)
              this.fileUploadQuestion[questionId] = 'fail'
              uploader.inUploadProgress = false
            }, (resp) => {
              const qiniuFileKey = resp && resp.key
              const currentStepQuestions = this.stepQuestions[`step${this.currentStep}`]
              const question = currentStepQuestions.find(item => item.id === questionId)
              const questionIndex = currentStepQuestions.indexOf(question)
              if (question && questionIndex > -1) {
                question.value = `${qiniuFileKey} ${fileName}`
                question.fileName = fileName
                this.stepQuestions[`step${this.currentStep}`][questionIndex] = question
                this.showMessage(this.$t('questionnaire.error.uploadSuccess'), 'success', 3)
                this.$nextTick(() => {
                  this.fileUploadQuestion[questionId] = 'finished'
                })
              }
              uploader.inUploadProgress = false
            }
          )
        }
      })
      // subscription.unsubscribe() // 上传取消
    },
    async getSingleStep (questionnaireId, stepNum, invitationId) {
      const stepId = this.steps[stepNum - 1].id
      const singleStepResp = await this.getStepQuestions({ questionnaireId: questionnaireId, stepId: stepId, invitationId: invitationId })
      if (singleStepResp && singleStepResp.questions) {
        const questions = singleStepResp.questions
        for (let index = 0; index < questions.length; index++) {
          this.applyPrefillValue(questions[index])
          if (questions[index].type === 'LINK' && !questions[index].value) {
            questions[index].value = '0'
          }
          if (questions[index].type === 'ATTACHMENT') {
            questions[index].file = ''
            const value = questions[index].value
            if (value && value.length > 0 && value.indexOf(' ') > -1) {
              const splitCharIndex = value.indexOf(' ')
              questions[index].name = value.slice(splitCharIndex + 1, value.length)
            }
            questions[index].uploadFunc = this.uploadFileForQuestion
          }
          if (questions[index].type === 'SINGLE') {
            const question = questions[index]
            const pid = question.pid
            if (pid) {
              let children = this.questionPidMap[pid]
              if (!children) {
                children = []
                this.questionPidMap[pid] = children
              }
              children.push(question)
            }
            if (question.has_dynamic_children) {
              const childrenQuestion = {}
              _.forEach(question.options, option => {
                childrenQuestion[option.id] = option.dynamic_children_question_ids.split(',').map(item => parseInt(item))
                _.forEach(childrenQuestion[option.id], item => {
                  if (!this.dynamicQuestionMap._allChildren.includes(item)) {
                    this.dynamicQuestionMap._allChildren.push(item)
                  }
                })
              })
              this.dynamicQuestionMap[question.id] = childrenQuestion
            }
            if (needSortOptionsQuestionId.includes(question.id)) {
              question.options = _.sortBy(question.options, item => item.text.en)
            }
          }
        }
        this.stepQuestions[`step${stepNum}`] = this.sortSteps(questions)
      }
    },
    applyPrefillValue (question) {
      if (question.prefill_value === null || question.prefill_value === undefined) {
        return
      }
      if (question.type === 'REPEAT_GROUP') {
        if (this.hasRepeatGroupAnswerRows(question.value)) {
          return
        }
        question.value = JSON.stringify({
          rows: this.getRepeatGroupRows(question.prefill_value, question.prefill_source)
        })
        return
      }
      if (question.value) {
        return
      }
      if (question.type === 'DATE_RANGE') {
        question.value = JSON.stringify(question.prefill_value)
      } else {
        question.value = question.prefill_value
      }
    },
    parseRepeatGroupValue (value) {
      if (!value) {
        return null
      }
      if (typeof value !== 'string') {
        return value
      }
      try {
        return JSON.parse(value)
      } catch (e) {
        return null
      }
    },
    getRepeatGroupRows (value, prefillSource) {
      const repeatGroupValue = this.parseRepeatGroupValue(value)
      if (_.isArray(repeatGroupValue)) {
        return repeatGroupValue
      }
      if (!repeatGroupValue) {
        return []
      }
      if (_.isArray(repeatGroupValue.rows)) {
        return repeatGroupValue.rows
      }
      if (prefillSource === 'resume_education' && _.isArray(repeatGroupValue.education)) {
        return repeatGroupValue.education
      }
      if (prefillSource === 'resume_work' && _.isArray(repeatGroupValue.work_experience)) {
        return repeatGroupValue.work_experience
      }
      if (_.isArray(repeatGroupValue.education)) {
        return repeatGroupValue.education
      }
      if (_.isArray(repeatGroupValue.work_experience)) {
        return repeatGroupValue.work_experience
      }
      return []
    },
    hasRepeatGroupAnswerRows (value) {
      return this.getRepeatGroupRows(value).length > 0
    },
    checkLinkQuestionHasDescription (question) {
      const config = question.config || {}
      return _.has(config, 'link_text_cn') || _.has(config, 'link_text_tc') || _.has(config, 'link_text_en')
    },
    getLinkConfigText (question, prefix) {
      return this.getLinkConfigValue(question, prefix) || ''
    },
    getLinkConfigValue (question, prefix) {
      const config = question.config || {}
      const options = question.options || []
      if (_.isEmpty(config) && options?.length > 0) {
        const option = options[0]
        return this.getText(option.text)
      }
      let locale = this.$i18n.locale
      if (locale === 'zh') {
        locale = 'cn'
      } else if (locale === 'hk' || locale === 'tw') {
        locale = 'tc'
      }
      return config[`${prefix}_${locale}`] || config[`${prefix}_en`] || config[`${prefix}_cn`] || config[`${prefix}_tc`] || config[prefix] || ''
    },
    async getSteps (questionnaireId) {
      const invitationInfo = this.$parent.invitationInfo
      if (invitationInfo && invitationInfo.is_finished) {
        const targetUrl = `/recruiting/questionnaire/${this.invitationId}/questionnaire-finished`
        this.$router.push({ path: targetUrl, query: this.$route.query })
        return
      }
      const resp = await this.getQuestionnaireSteps(questionnaireId)
      this.steps = (resp && resp.steps && this.sortSteps(resp.steps)) || []
    },
    validateForm () {
      const currentQuestionIds = this.getStepQuestionIds(this.currentStep)
      let result = true
      let nationalIDIssueDate = 0
      let nationalIDExpireDate = 0
      let birthDate = 0
      for (const id of currentQuestionIds) {
        let ref = this.$refs[`question${id}`]
        if (ref && ref.length > 0) {
          ref = ref[0]
        }
        if (!ref) {
          continue
        }
        if (ref.$el.parentElement.style.display === 'none') {
          continue
        }
        if (!ref.validate()) {
          result = false
        }
        if (id === 8) {
          birthDate = ref.internalValue
        }
        if (id === 29) {
          nationalIDIssueDate = ref.internalValue
        }
        if (id === 30) {
          nationalIDExpireDate = ref.internalValue
        }
      }
      if (birthDate) {
        const now = new Date()
        // at least 16 year old
        const smallestBirthYearDate = time.getDayZeroDate(now.setFullYear(now.getFullYear() - 16).valueOf())
        if (birthDate > smallestBirthYearDate) {
          result = false
          this.showMessage(this.$t('questionnaire.error.ageTooSmall'), 'error', 3)
        }
      }
      nationalIDIssueDate = this.normalizeDateValue(nationalIDIssueDate)
      nationalIDExpireDate = this.normalizeDateValue(nationalIDExpireDate)
      if (nationalIDIssueDate && nationalIDExpireDate) {
        if (nationalIDExpireDate <= nationalIDIssueDate) {
          result = false
          this.showMessage(this.$t('questionnaire.error.nationalIDExpireLargeThanIssue'), 'error', 3)
        } else if (nationalIDExpireDate < moment().valueOf()) {
          result = false
          this.showMessage(this.$t('questionnaire.error.nationalIDExpireLargeThanNow'), 'error', 3)
        }
      }
      return result
    },
    normalizeDateValue (value) {
      if (typeof value !== 'string') {
        return value
      }
      return moment(value.replace(/\//g, '-')).valueOf()
    },
    getStepQuestionIds (stepId) {
      const questions = this.stepQuestions[`step${stepId}`]
      if (!questions) {
        return []
      }
      return questions.map((question) => question.id)
    },
    async submitAnswers (isLastPage, willClose = false) {
      const answers = []
      const currentQuestions = this.stepQuestions[`step${(this.currentStep)}`]
      for (const question of currentQuestions) {
        const id = question.id
        let ref = this.$refs[`question${id}`]
        if (ref && ref.length > 0) {
          ref = ref[0]
        }
        if (!ref) {
          continue
        }
        if (ref.$el.parentElement.style.display === 'none') {
          continue
        }
        let value = question.value
        if (question.type === 'SIGN') {
          try {
            value = await ref.persist()
            question.value = value
          } catch (e) {
            this.onSignatureUploadError()
            return false
          }
        }
        const uploadStatus = this.fileUploadQuestion[id]
        if (uploadStatus === 'changed') {
          value = ref.value
        }
        answers.push({ question_id: id, value: value })
      }
      const params = {
        questionnaireId: this.questionnaireId,
        answers: answers,
        isLastPage: isLastPage,
        invitationCode: this.$parent.invitationCode
      }
      const resp = await this.answerQuestions(params)
      if (resp && (willClose || isLastPage)) {
        this.$parent.invitationInfo.is_finished = true
        const pagePath = `/recruiting/questionnaire/${this.invitationId}/questionnaire-finished`
        this.$router.push({ path: pagePath, query: this.$route.query })
      }
      return true
    },
    toLastStep () {
      if (this.currentStep === 0) {
        return
      }
      this.fileUploadQuestion = {}
      this.$router.push(`${this.$route.path}?step=${this.currentStep - 1}&invitationCode=${this.invitationCode}`)
    },
    handleCancelBtnClick () {
      this.$router.push({ path: this.$parent.getQuestionnaireHomePath(), query: this.$route.query })
      const path = this.$parent.getQuestionnaireHomePath() + `?step=${this.currentStep - 1}&invitationCode=${this.invitationCode}`
      this.$router.push(path)
    },
    async toNextStep () {
      for (const questionId in this.fileUploadQuestion) {
        const uploadStatus = this.fileUploadQuestion[questionId]
        if (uploadStatus === 'changed') {
          if (!confirm(this.$t('questionnaire.message.fileChangedButNoUpload'))) {
            return
          }
        }
      }
      if (!this.validateForm()) {
        return
      }
      if (this.steps.length === 0) {
        this.showMessage(this.$t('questionnaire.error.noSteps'), 'error', 3)
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        if (this.currentStep > 0) {
          const result = await this.submitAnswers(this.currentStep === this.steps.length)
          if (result === false) {
            this.showMessage(this.$t('questionnaire.error.tryAgain'), 'error', 3)
            return
          }
        }
        if (this.currentStep < this.steps.length) {
          const page = this.currentStep + 1
          if (page > 0 && page - 1 < this.steps.length) {
            await this.getSingleStep(this.questionnaireId, (page), this.invitationId)
          }
          this.$router.push(`${this.$route.path}?step=${page}&invitationCode=${this.invitationCode}`)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    onDropdownChange (selectedValue, question) {
      const id = question.id
      const children = this.questionPidMap[id]
      if (children && children.length > 0) {
        const selectOption = question.options.find(item => item.id === selectedValue)
        const optionId = selectOption.id
        children.forEach(child => {
          const childDropdown = this.$refs[`question${child.id}`][0]
          let options = child.options.filter(item => item.pid === optionId)
          options = this.getOptions(options)
          options.forEach(item => { item.isShow = true })
          childDropdown.displayText = null
          childDropdown.inputValue = null
          childDropdown.internalValue = null
          childDropdown.showItem = false
          childDropdown.searchedOptions = options
        })
      }
      if (question.has_dynamic_children) {
        const childQuestions = this.dynamicQuestionMap[id][selectedValue]
        _.forEach(this.dynamicQuestionMap._allChildren, questionId => {
          let questionRef = this.$refs[`question${questionId}`]
          if (questionRef && questionRef.length > 0) {
            questionRef = questionRef[0]
            const style = questionRef.$el.parentElement.style
            if (childQuestions.indexOf(questionId) > -1) {
              style.display = null
            } else {
              style.display = 'none'
            }
          }
        })
      }
    },
    showQuestion (allquestion, question) {
      if (this.dynamicQuestionMap._allChildren.includes(question.id)) {
        for (const parentId in this.dynamicQuestionMap) {
          const parent = _.find(allquestion, { id: parseInt(parentId) })
          if (parent) {
            const childrenQuestionMap = this.dynamicQuestionMap[parentId]
            const optionId = parent.value
            const childrenQuestion = childrenQuestionMap[optionId]
            return childrenQuestion && childrenQuestion.includes(question.id)
          }
        }
        return false
      }
      return true
    },
    handleFileChange (event, question) {
      this.fileUploadQuestion[question.id] = 'changed'
    },
    onStarsChange (changedVaule, question) {
      if (changedVaule <= 6) {
        question.value = changedVaule
      }
    },
    onNPSChange (changedValue, question) {
      if (changedValue <= 11) {
        question.value = changedValue
      }
    },
    onSignatureUploadError () {
      this.showMessage(this.$t('questionnaire.error.signatureUploadFailed'), 'error', 3)
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/scss/onboarding-common.scss';
  .paperwork-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    .banner {
        img {
          background-size: cover;
          width: 100%;
          height: auto;
        }
    }
    .message {
      position: fixed;
      top: 50px;
    }
    .title {
      font-weight: bolder;
      margin-top: 2rem;
      margin-bottom: 1.2rem;
      text-align: center;
    }
    .subtitle {
      text-align: center;
      color: gray;
    }
    .divider {
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 2.5rem;
      float: none;
    }
    .empty-div {
      height: 50px;
    }
    .step-zero-div {
      width: 75%;
      color: gray;

      li {
        margin: 2rem 0;
        list-style-type: disc;
        font-size: smaller !important;
      }
    }
    .step-notice {
      width: 90%;
      margin: 2rem auto;
      text-align: center;
      font-weight: bolder;
    }
    .steps-component {
      margin-bottom: 20px;
    }
    .input-form-div {
      width: 100%;
      padding: 0 5%;
    }
    .next-button {
      width: 100%;
      margin: 4rem 0rem 4rem 0;
      padding-bottom: 100px;
      display: flex;
      justify-content: space-around;
      align-items: center;

      .btn-next, .btn-cancel, .btn-back {
        width: 25%;
        min-width: 100px;
      }
    }
  }
/* 小型设备（平板电脑，768px 起） */
@media (min-width: 768px) {
    .next-button {
      width: 40% !important;
    }
    .steps-component {
      margin-bottom: 100px !important;
    }
    .step-zero-div {
      width: 40% !important;
      li {
        list-style-type: disc;
        margin-top: 30px !important;
        font-size: smaller !important;
      }
    }
}
</style>
